import { ColorPickerPropsBase } from "components/ColorPicker/ColorPicker";
import { Button, Flex, Typography } from "antd";
import { Tooltip } from "@libertify/ui";
import { PaintBucketIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme/";
import { normalizeGradientForColorPicker } from "../../helpers/itemsHelper";
import { useMemo } from "react";
import { PaletteResponse } from "pages/media/types/";
import { isTransparentHex } from "../../helpers";
import { DEFAULT_COLOR, DEFAULT_TEXT_COLOR } from "../../constants";
import { PlaceholderColorPicker } from "./PlaceholderColorPicker";
import { GradientDefinition } from "types";

interface IconColorPickerProps extends Omit<
  ColorPickerPropsBase,
  "onChangeComplete" | "value"
> {
  title: string;
  type: "text" | "background" | "stroke";
  hint?: string;
  palette: PaletteResponse;
  onChangeComplete: (color: string | GradientDefinition | undefined) => void;
  value?: string | GradientDefinition | null;
}

export const IconColorPicker = ({
  title,
  type,
  hint,
  palette,
  onChangeComplete,
  ...colorPickerProps
}: IconColorPickerProps) => {
  const { theme } = useAntTheme();
  const displayColor = useMemo(() => {
    const value = normalizeGradientForColorPicker(
      colorPickerProps.value ?? undefined,
      palette
    );

    if (
      typeof value === "string" &&
      value.startsWith("#") &&
      isTransparentHex(value)
    ) {
      return DEFAULT_TEXT_COLOR;
    }

    return value;
  }, [colorPickerProps.value, palette]);
  const icon = {
    text: (
      <Typography.Text style={{ fontSize: 20, fontWeight: 500, lineHeight: 1 }}>
        A
      </Typography.Text>
    ),
    background: (
      <PaintBucketIcon
        size={20}
        data-testid="paint-bucket-icon"
        color={DEFAULT_TEXT_COLOR}
        style={{ verticalAlign: "text-top", paddingBottom: 2 }}
      />
    ),
    stroke: (
      <Typography.Text
        style={{
          fontSize: 20,
          fontWeight: 500,
          lineHeight: 1,
          WebkitTextStroke: `1px ${DEFAULT_TEXT_COLOR}`,
          color: "transparent",
        }}
      >
        A
      </Typography.Text>
    ),
  };

  const isWhite = useMemo(() => {
    if (!displayColor || typeof displayColor !== "string") return false;
    const color = displayColor.slice(0, 7);
    return color === DEFAULT_COLOR;
  }, [displayColor]);

  return (
    <Flex vertical justify="center" align="center">
      <PlaceholderColorPicker
        title={title}
        palette={palette}
        {...colorPickerProps}
        onChangeComplete={(color) => {
          if (typeof color === "string") onChangeComplete(color);
        }}
      >
        <Tooltip content={hint}>
          <Button
            type="text"
            icon={
              <Flex
                vertical
                justify="center"
                align="center"
                style={{ height: 32, cursor: "pointer" }}
              >
                {icon[type]}
                <div
                  style={{
                    width: 15,
                    height: 2,
                    backgroundColor:
                      typeof displayColor === "string"
                        ? displayColor
                        : displayColor?.[0].color,
                    border: isWhite ? `1px solid ${theme.colorGrey}` : "none",
                  }}
                />
              </Flex>
            }
          />
        </Tooltip>
      </PlaceholderColorPicker>
    </Flex>
  );
};
